<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import {
    dialogTypes,
    getWithJwt,
    isClient,
    postWithJwt,
    serverlessRoutes,
    translate,
    errorTracker,
  } from "lib";
  import {
    cantDoExercises,
    currentClient,
    dialogData,
    showAlert,
    trainerClientPrograms,
    trainerClientWorkouts,
    user,
  } from "stores";
  import WorkoutExpansionPanel from "../../../components/UI/WorkoutExpansionPanel.svelte";
  import InfiniteScroll from "../../../components/UI/InfiniteScroll.svelte";
  import ButtonGroup from "../../../components/UI/ButtonGroup.svelte";
  import ProgramComponent from "../../../components/Training/ProgramComponent.svelte";
  import type { IButtonGroup } from "src/interfaces/IButtonGroup";
  import { querystring, replace } from "svelte-spa-router";
  import { ButtonComponent } from "ui";
  import { Capacitor } from "@capacitor/core";

  // AI data for client's finished workouts (web + TRAINER only).
  // Mirrors the pattern used in ProgramScreen.svelte so the red/green
  // AI stick and the AI note + "Proverio trener" checkbox render in the
  // DIARY tab exactly like they do on the assigned workout.
  const AI_TRAINER_ALLOWLIST = [13, 87649, 17833];
  let workoutAiData: { [key: number]: { hasAiData: boolean; aiData: any } } = {};

  const typeMap: { [key: string]: "ACTIVE" | "DIARY" } = {
    active: "ACTIVE",
    diary: "DIARY",
  };
  const queryObject: URLSearchParams = new URLSearchParams($querystring);
  const queryIterator: IterableIterator<string> = queryObject.values();
  queryIterator.next();
  const selectedType: string = queryIterator.next().value;

  let type: "ACTIVE" | "DIARY" = $querystring === "" ? "ACTIVE" : typeMap[selectedType];

  const fetchWorkoutAiData = async (): Promise<void> => {
    // Web only — skip on native (iOS/Android).
    if (Capacitor.isNativePlatform()) return;

    // TRAINER only — never run for clients (route already gates this, but be defensive).
    if (isClient($user)) return;

    // Restrict to the trainer accounts that have the AI feature enabled,
    // matching the allowlist used in ProgramScreen.svelte.
    if (!AI_TRAINER_ALLOWLIST.includes($user.id)) return;

    const workouts = $trainerClientWorkouts.data;
    if (!workouts || workouts.length === 0) return;

    const workoutIds = workouts.map((workout: any) => workout.id);

    try {
      const response = await postWithJwt(`${serverlessRoutes.WORKOUT}/check-ai-data`, {
        workoutIds,
      });

      if (response.data && response.data.workoutAiData) {
        const next: { [key: number]: { hasAiData: boolean; aiData: any } } = { ...workoutAiData };
        response.data.workoutAiData.forEach((item: any) => {
          next[item.workoutId] = {
            hasAiData: item.hasAiData,
            aiData: item.aiData,
          };
        });
        workoutAiData = next; // Trigger reactivity
      }
    } catch (error) {
      errorTracker.captureError(error as Error | string, "error", {
        context: "client_profile",
        action: "fetch_workout_ai_data",
        clientId: $currentClient?.id,
      });
    }
  };

  const fetchPrograms = async (): Promise<void> => {
    try {
      const response = await getWithJwt(
        `${serverlessRoutes.PROGRAM}/v2?clientId=${$currentClient.id}`
      );
      $trainerClientPrograms = response.data;
      // $countersStore.trainerClientPrograms = response.count;
    } catch (error) {
      errorTracker.captureError(error as Error | string, "error", {
        context: "client_profile",
        action: "fetch_programs",
        clientId: $currentClient.id,
      });
    }
  };

  const onActive = async (): Promise<void> => {
    if (type === "ACTIVE") {
      return;
    }

    type = "ACTIVE";
    // setType("active");
    await replace(`/profile/${$currentClient.id}?view=training&type=active`);
  };

  const onDiary = async (): Promise<void> => {
    if (type === "DIARY") {
      return;
    }

    type = "DIARY";
    // setType("diary");
    await replace(`/profile/${$currentClient.id}?view=training&type=diary`);
  };

  const onCreateProgram = (): void => {
    $dialogData.data = { clientId: $currentClient.id };
    $dialogData.type = dialogTypes.CREATE_EDIT_PROGRAM;
  };

  const onAssignTrainingProgram = (): void => {
    $dialogData.data = {
      client: $currentClient,
    };

    $dialogData.type = dialogTypes.ASSIGN_WORKOUT_PLAN;
  };

  const submit = async (data) => {
    const url = serverlessRoutes.WORKOUT;
    const partialWorkout = {
      name: data.name,
      // description: data.description,
      entityType: 0,
      programId: $trainerClientPrograms[0].id,
      protocoll: null,
      showMacros: true,
      finishedAt: data.finishedAt
        ? new Date(data.finishedAt).toISOString()
        : new Date().toISOString(),
    };
    try {
      const res = await postWithJwt(url, { partialWorkout });
      $trainerClientWorkouts.data = [res.data.workout, ...$trainerClientWorkouts.data];

      $dialogData.type = "";
      $dialogData.data = {};

      $showAlert.message = `${translate("SUCCESSFULLY_CREATED")} ${translate(
        "WORKOUT"
      ).toLowerCase()}.`;
    } catch (err) {}
  };
  const onAssignWorkout = (): void => {
    $dialogData.data = { executeFunction: submit };
    $dialogData.type = dialogTypes.CREATE_EDIT_FINISHED_WORKOUT;
  };

  let selectedButtonIndex = 0;

  // let cantDo: Array<any> = [];

  onMount(async (): Promise<void> => {
    const { href } = window.location;

    if (href.includes("type=active")) {
      selectedButtonIndex = 0;
    } else if (href.includes("type=diary")) {
      selectedButtonIndex = 1;
    }

    await fetchPrograms();
    await trainerClientWorkouts.fetchData();

    if (window.location.href.includes("/profile") && !isClient($user)) {
      const userId = window.location.href.split("/")[5];
      const workoutIds = $trainerClientWorkouts.data.map((workout) => workout.id);

      const response = await getWithJwt(
        `${serverlessRoutes.WORKOUT}/cant-do-exercises?workoutIds=%5B${workoutIds.join(",")}%5D&userId=${userId}`
      );
      $cantDoExercises.workoutIds = [...$cantDoExercises.workoutIds, ...response.data.cantDo];
      // cantDo = response.data.cantDo;
    }

    // Fetch AI data for the just-loaded journal workouts. Internally guarded
    // to web + TRAINER + allowlisted trainer ids so it no-ops elsewhere.
    await fetchWorkoutAiData();
  });

  const onLoadMoreDiary = async (): Promise<void> => {
    await trainerClientWorkouts.fetchMoreData();
    // Re-fetch AI data so newly paginated workouts get their stick + note.
    await fetchWorkoutAiData();
  };

  const buttonGroup: IButtonGroup[] = [
    {
      title: translate("ACTIVE"),
      onClick: onActive,
    },
    {
      title: translate("DIARY"),
      onClick: onDiary,
    },
  ];

  onDestroy(() => {
    $trainerClientWorkouts.data = [];
  });
</script>

<div class="flex flex-col gap-4">
  <div class="flex justify-center">
    <ButtonGroup buttons={buttonGroup} selectedIndex={selectedButtonIndex} />
  </div>

  {#if type === "ACTIVE"}
    <div class="flex justify-between">
      <ButtonComponent on:click={onCreateProgram}>
        {translate("CREATE")}
        {translate("PROGRAM").toLowerCase()}
      </ButtonComponent>
      <ButtonComponent on:click={onAssignTrainingProgram}>
        {translate("ASSIGN_TRAINING_PROGRAM")}
      </ButtonComponent>
    </div>

    {#each $trainerClientPrograms as program}
      <ProgramComponent {program} />
    {/each}

    <!-- <InfiniteScroll
      hasMore={$trainerClientPrograms.length <
        $countersStore.trainerClientPrograms}
      on:loadMore={onLoadMorePrograms}
    /> -->
  {:else}
    <div class="flex justify-between">
      <!-- <ButtonComponent on:click={onCreateProgram}>
        {translate("EXPORT_TO_PDF")}
      </ButtonComponent> -->
      <ButtonComponent on:click={onAssignWorkout}>
        {translate("ADD")}
        {translate("WORKOUT").toLowerCase()}
      </ButtonComponent>
    </div>

    {#each $trainerClientWorkouts.data as workout}
      <WorkoutExpansionPanel
        {workout}
        showComments
        isFinished={workout.finishedAt !== null}
        aiData={workoutAiData[workout.id]}
      />
    {/each}

    <InfiniteScroll
      isFetchingMore={$trainerClientWorkouts.isFetchingMore}
      hasMore={$trainerClientWorkouts.hasMore}
      on:loadMore={onLoadMoreDiary}
    />
  {/if}
</div>
